"use client";
import { getRiseAuthorizationHeader } from "@/auth/session";
import { getSponsorshipUrl } from "@/lib/config";

/**
 * Minimal HTTP client for the Phoenix sponsorship service. Matches the shape of
 * phoenix-v2-sdk's `V1SponsorshipClient` for the two endpoints we need here.
 *
 * Paths + request/response shapes are camelCase, as defined by
 * `GetFeePayerRequestSchema` / `SponsorTransactionRequestSchema` in the SDK.
 */

export interface GetFeePayerRequest {
  userPubkey: string;
}

export interface GetFeePayerResponse {
  payer: string;
}

export interface SponsorTransactionRequest {
  /** Base64-encoded signed transaction bytes. */
  transaction: string;
  /** Base58-encoded user signature extracted from the signed transaction. */
  userSignature: string;
  /** The user's wallet pubkey (base58). */
  userPubkey: string;
  /** Slot at which the blockhash is fresh, as a stringified number. */
  slot: string;
  /** Optional — trader pubkey when signing on behalf of a delegated trader. */
  traderPubkey?: string;
  /** Optional — authority pubkey that owns the position. */
  positionAuthority?: string;
}

export interface SponsorTransactionResponse {
  signature: string;
}

export class SponsorshipError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown
  ) {
    super(message);
    this.name = "SponsorshipError";
  }
}

const hasProperty = <TKey extends string>(
  value: unknown,
  key: TKey
): value is Record<TKey, unknown> =>
  value !== null && typeof value === "object" && key in value;

const hasStringProperty = <TKey extends string>(
  value: unknown,
  key: TKey
): value is Record<TKey, string> => {
  if (!hasProperty(value, key)) return false;
  return typeof value[key] === "string";
};

const getStringProperty = (value: unknown, key: string): string | null =>
  hasStringProperty(value, key) ? value[key] : null;

const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const parseGetFeePayerResponse = (value: unknown): GetFeePayerResponse => {
  const payer = getStringProperty(value, "payer");
  if (payer !== null) return { payer };
  throw new SponsorshipError("Invalid fee payer response", 0, value);
};

const parseSponsorTransactionResponse = (
  value: unknown
): SponsorTransactionResponse => {
  const signature = getStringProperty(value, "signature");
  if (signature !== null) return { signature };
  throw new SponsorshipError("Invalid sponsorship response", 0, value);
};

const postJson = async <TResponse>(
  path: string,
  body: unknown,
  authAuthority: string,
  parseResponse: (value: unknown) => TResponse
): Promise<TResponse> => {
  const baseUrl = getSponsorshipUrl();
  if (!baseUrl) {
    throw new SponsorshipError(
      "Sponsorship URL not configured: set NEXT_PUBLIC_API_URL",
      0
    );
  }

  const requestBody = JSON.stringify(body);
  const send = async (forceRefresh: boolean): Promise<Response> => {
    const authorization = await getRiseAuthorizationHeader({
      authority: authAuthority,
      forceRefresh,
    });
    if (authorization === null) {
      throw new SponsorshipError(
        "Authentication session missing for this wallet. Reconnect your wallet and try again.",
        401
      );
    }

    return fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: authorization,
      },
      body: requestBody,
    });
  };

  let response = await send(false);
  if (response.status === 401 || response.status === 403) {
    response = await send(true);
  }

  const parsed = await readJson(response);

  if (!response.ok) {
    const errMessage =
      getStringProperty(parsed, "message") ??
      getStringProperty(parsed, "error") ??
      `Sponsorship request failed (HTTP ${response.status})`;
    throw new SponsorshipError(errMessage, response.status, parsed);
  }

  return parseResponse(parsed);
};

export const getFeePayer = (
  request: GetFeePayerRequest
): Promise<GetFeePayerResponse> =>
  postJson<GetFeePayerResponse>(
    "/v1/sponsorship/fee-payer",
    request,
    request.userPubkey,
    parseGetFeePayerResponse
  );

export const sponsorTransaction = (
  request: SponsorTransactionRequest
): Promise<SponsorTransactionResponse> =>
  postJson<SponsorTransactionResponse>(
    "/v1/sponsorship/transaction",
    request,
    request.userPubkey,
    parseSponsorTransactionResponse
  );
